<template>
  <div class="c-editor-content-link">
    <div
      class="document-button text-transform-none inline-block border-style-solid border-primary5 border-width-1 rounded-1 pa-1 pl-3 bg-primary6_1"
    >
      <CIcon class="mr-2" icon="file" />
      <span>{{ selectedDocument ? selectedDocument.text : t('canvasUI.CContentLinkToolbar.noDocumentSelected') }}</span>
      <CButton v-if="selectedDocument" circle class="ml-3" size="small" @click="openContentSelector">
        <CIcon icon="pen" />
      </CButton>
      <CButton v-if="selectedDocument" circle class="ml-1" size="small" @click="previewDocument">
        <CIcon icon="eye" />
      </CButton>
    </div>
    <CButton v-if="selectedDocument" circle class="ml-2 clear-button" size="small" @click="removeLink">
      <CIcon icon="circle-xmark" />
    </CButton>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Editor } from '@tiptap/vue-3'
import useContentSelector from '@canvas-builder/composables/useContentSelector'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { type SelectedPage, type SelectedFile } from '@content-selector/stores/app'
import { CONTENT_LINK_PREFIX } from '@lib/components/CRichTextEditor/extensions/contentLink'

interface Props {
  editor: Editor
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  openFile: [{ fileId: string; pageIndex?: number }]
}>()
const { t } = useI18n()

const { setContentSelectorMode } = useContentSelector()

interface SelectedDocument {
  target: string
  page?: number
  text: string
}

const selectedDocument = ref<SelectedDocument | null>(null)

onMounted(() => {
  const linkAttrs = props.editor.getAttributes('contentLink')
  if (linkAttrs.target) {
    selectedDocument.value = {
      target: linkAttrs.target,
      text: linkAttrs.text || 'Document ' + linkAttrs.target,
      page: linkAttrs.page,
    }
  } else {
    openContentSelector()
  }
})

watch(
  () => props.editor.getAttributes('contentLink'),
  (newLink: Record<string, any>) => {
    if (newLink && typeof newLink === 'object' && newLink.target && newLink.target !== selectedDocument.value?.target) {
      selectedDocument.value = {
        target: newLink.target as string,
        text: (newLink.text || `Document ${newLink.target}`) as string,
        page: Number(newLink.page),
      }
    }
  },
)

function removeLink() {
  props.editor.chain().focus().unsetContentLink().run()
  selectedDocument.value = null
  emit('close')
}

async function openContentSelector() {
  try {
    setContentSelectorMode({
      id: null,
      componentType: null,
      selections: selectedDocument.value ? [{ fileId: selectedDocument.value.target, type: 'file' }] : [],
      allowedSelectorTypes: ['content'],
      maxSelections: 1,
      onSelected: (selections: (SelectedPage | SelectedFile)[]) => {
        if (selections.length > 0) {
          const selection = selections[0]
          if ('page_index' in selection) {
            selectedDocument.value = {
              target: selection.file.id,
              text: `${selection.file.name} - ${selection.name}`,
              page: selection.page_index,
            }
          } else {
            selectedDocument.value = {
              target: selection.id,
              text: selection.name,
            }
          }

          const isInsideContentLink = props.editor.isActive('contentLink')
          if (props.editor.state.selection.empty && !isInsideContentLink) {
            // Insert the URL as text if no text is selected, note the extra space after the link
            const href = `${CONTENT_LINK_PREFIX}${selectedDocument.value.target}`
            const pageParam = selectedDocument.value.page ? `&page=${selectedDocument.value.page}` : ''
            const linkContent = `<a data-type="content-link" data-text="${selectedDocument.value.text}" href="${href}${pageParam}">${selectedDocument.value.text}</a> `
            props.editor.chain().focus().insertContent(linkContent).run()
          } else {
            props.editor
              .chain()
              .focus()
              .extendMarkRange('contentLink')
              .setContentLink({
                target: selectedDocument.value.target,
                text: selectedDocument.value.text,
                page: selectedDocument.value.page,
              })
              .run()
          }
        }
      },
    })
  } catch (error) {
    console.error('Error selecting content:', error)
  }

  emit('close')
}

function previewDocument() {
  if (selectedDocument.value?.target) {
    emit('openFile', {
      fileId: selectedDocument.value.target,
      pageIndex: selectedDocument.value.page ?? undefined,
    })
  }
}
</script>
